/**
 * The hospital-affiliated doctor's locked features.
 *
 * A doctor employed by a hospital keeps the ordinary doctor account — same
 * role, same screens, still bookable through the patient catalogue. What they
 * lose is the private-practice business: their own clinics, offers, discount
 * codes, marketing packages and photography sessions, all of which bill *their*
 * money and mean nothing when the hospital owns the diary.
 *
 * The requirement is specific that those tabs stay **visible but disabled**,
 * and that clicking one explains why and offers a way out. So this is not a
 * route guard: a guard would remove the row and the doctor would never learn
 * that the product they are being sold exists.
 *
 * It renders from a missing capability rather than a role check. There is no
 * `if (isHospitalDoctor)` anywhere — the doctor simply does not hold
 * PERSONAL_CLINIC_MANAGE, which is one condition rather than five hard-coded
 * tab names, and which means a hospital that later wants one of its doctors to
 * keep a private practice grants that permission and every lock opens at once.
 */

import { useState } from 'react';
import { Button, Icon, Modal } from '../../components/common';
import { usePermissions } from '../../context/PermissionContext';
import { PERMISSIONS } from '../../domain/rbac';

/**
 * The phone number the modal offers.
 *
 * Configured at build time because it is the platform's own sales line, the
 * same for every hospital, and putting it behind an API call would mean the
 * modal could fail to render the one piece of information it exists to deliver.
 */
const ADMIN_PHONE = import.meta.env.VITE_SUPER_ADMIN_PHONE || '';

/**
 * Does this session hold the private-practice business?
 *
 * Returns `{ locked, loading }`. `loading` matters: PermissionContext fails
 * closed, so during the first fetch every capability reads false, and a screen
 * that treated that as "locked" would flash the upsell at an independent doctor
 * on every slow connection.
 */
export function usePersonalClinicAccess() {
  const { can, loading } = usePermissions();
  return { locked: !loading && !can(PERMISSIONS.PERSONAL_CLINIC_MANAGE), loading };
}

/**
 * The modal itself.
 *
 * Wording is the requirement's, translated: this is a hospital-affiliated
 * account, personal clinic management is disabled, and here is who to call.
 */
export function HospitalRestrictionModal({ open, onClose, feature }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      icon="lock"
      title="خاصية غير متاحة لحساب المستشفى"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            حسناً
          </Button>
          {ADMIN_PHONE && (
            <a href={`tel:${ADMIN_PHONE}`} className="btn-primary">
              <Icon name="phone" size={16} /> اتصل بالإدارة
            </a>
          )}
        </>
      }
    >
      <div className="space-y-4 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10 text-amber-400">
          <Icon name="lock" size={26} />
        </div>

        <p className="text-sm leading-relaxed text-slate-300">
          هذا حساب تابع لمنشأة طبية، وخصائص إدارة العيادة الخاصة
          {feature ? ` (${feature})` : ''} معطّلة فيه.
        </p>

        <p className="text-sm leading-relaxed text-slate-300">
          للحصول على نظام رقمي خاص بعيادتك الشخصية، برجاء التواصل مع الإدارة
          {ADMIN_PHONE ? (
            <>
              {' '}
              على الرقم{' '}
              <span dir="ltr" className="font-mono font-bold text-cyan-300">
                {ADMIN_PHONE}
              </span>
            </>
          ) : (
            ''
          )}
          .
        </p>
      </div>
    </Modal>
  );
}

/**
 * Wraps a nav row or a tab so it reads as present-but-locked.
 *
 * Deliberately keeps the child rendered and visible. Greying it out and
 * swallowing the click is the whole point — the doctor must be able to see that
 * the feature exists.
 */
export function LockedFeature({ feature, children, className = '' }) {
  const { locked } = usePersonalClinicAccess();
  const [open, setOpen] = useState(false);

  if (!locked) return children;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-disabled="true"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={`relative cursor-pointer opacity-55 grayscale transition-opacity hover:opacity-75 ${className}`}
      >
        {/* Swallows every pointer event inside, so a nested link or button
            cannot navigate past the lock. */}
        <div className="pointer-events-none">{children}</div>
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400">
          <Icon name="lock" size={13} />
        </span>
      </div>

      <HospitalRestrictionModal
        open={open}
        onClose={() => setOpen(false)}
        feature={feature}
      />
    </>
  );
}

/**
 * A whole page's worth of the same message, for a route a doctor reaches by
 * typing the URL rather than by clicking a locked row.
 */
export function LockedPage({ feature }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 text-amber-400">
        <Icon name="lock" size={30} />
      </div>
      <h1 className="mb-2 text-lg font-bold text-white">
        {feature || 'هذه الخاصية'} غير متاحة لحساب المستشفى
      </h1>
      <p className="max-w-md text-sm text-slate-400">
        هذا حساب تابع لمنشأة طبية. للحصول على نظام رقمي خاص بعيادتك الشخصية، تواصل
        مع الإدارة
        {ADMIN_PHONE ? (
          <>
            {' '}
            على{' '}
            <span dir="ltr" className="font-mono text-cyan-300">
              {ADMIN_PHONE}
            </span>
          </>
        ) : (
          ''
        )}
        .
      </p>
      <Button className="mt-5" onClick={() => setOpen(true)}>
        تفاصيل
      </Button>

      <HospitalRestrictionModal open={open} onClose={() => setOpen(false)} feature={feature} />
    </div>
  );
}

export default HospitalRestrictionModal;
